'use client'

import { useRef, useState, type CSSProperties, type HTMLAttributes } from 'react'
import { useInView, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'

/**
 * Reveals heading text with a one-shot chroma band sweep the first time it
 * scrolls into view.
 *
 * Wrap the WHOLE heading content in a single ChromaText so the sweep travels
 * the full sentence as one wave; children keep their own fills (solid colors,
 * gradient spans, underlines, …). Two synchronized layers do the work: the
 * real text under an animated reveal mask, and an aria-hidden duplicate
 * filled with the brand chroma gradient, masked to a narrow window that rides
 * the reveal edge. Both masks share the same geometry and timing, so the band
 * and the reveal can never drift apart.
 *
 * The band layer is mounted ONLY while the sweep is running. It is a second
 * copy of the heading's glyphs, so leaving it in the tree put every headline
 * into the document twice — search engines read `aria-hidden` text, and an h1
 * that reads "Campus ERP…Campus ERP…" is the site's strongest on-page signal
 * stated twice. Gating it on `inView` keeps it out of the server-rendered HTML
 * (where `inView` is always false), and unmounting on `animationend` keeps it
 * out of the settled DOM. Nothing is lost visually: the band's resting mask is
 * fully transparent, so once the animation completes the layer already paints
 * nothing.
 *
 * `from` / `via` / `to` are a convenience for headings whose entire text
 * rests on a gradient fill.
 */

const CHROMA_FILL = 'linear-gradient(90deg, #37ecb0, #2dd4bf, #7dd3fc, #0ea5e9)'

// Both masks are 3 windows wide; the keyframes slide them from the
// transparent end (100%) to the resting end (0), so the visible window moves
// through [hidden → band/fade → settled] and the text develops left to right.
// The resting window [0, 33.33%] is fully opaque (reveal) / fully clear
// (band) so nothing lingers once the animation completes.
const REVEAL_MASK =
  'linear-gradient(90deg, #000 0%, #000 38%, rgba(0,0,0,0.7) 48%, transparent 62%, transparent 100%)'
const BAND_MASK =
  'linear-gradient(90deg, transparent 0%, transparent 36%, #000 45%, #000 52%, transparent 62%, transparent 100%)'

// Masks clip at the border box, so tight line-heights would crop ascenders
// and descenders. Each layer gets padding to enclose the glyph overshoot; the
// wrapper's negative margins hand that space back to the layout.
const LAYER_PAD = 'px-[0.1em] py-[0.2em]'

function maskLayer(image: string): CSSProperties {
  return {
    WebkitMaskImage: image,
    maskImage: image,
    WebkitMaskSize: '300% 100%',
    maskSize: '300% 100%',
    WebkitMaskPosition: '100% 0',
    maskPosition: '100% 0',
  }
}

interface ChromaTextProps extends HTMLAttributes<HTMLSpanElement> {
  /** Resting gradient start — omit to keep the children's own fills */
  from?: string
  /** Optional resting gradient midpoint */
  via?: string
  /** Resting gradient end — defaults to `from` */
  to?: string
}

export function ChromaText({
  from,
  via,
  to,
  className,
  style,
  children,
  ...props
}: ChromaTextProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const reducedMotion = useReducedMotion()
  const [sweepDone, setSweepDone] = useState(false)

  const fillClass = from
    ? 'bg-clip-text [-webkit-text-fill-color:transparent]'
    : undefined
  const fillStyle: CSSProperties | undefined = from
    ? {
        backgroundImage: `linear-gradient(90deg, ${from}${via ? `, ${via}` : ''}, ${to ?? from})`,
      }
    : undefined

  if (reducedMotion) {
    return (
      <span
        {...props}
        className={cn('inline-block', fillClass, className)}
        style={{ ...fillStyle, ...style }}
      >
        {children}
      </span>
    )
  }

  return (
    <span
      ref={ref}
      {...props}
      className={cn('relative inline-block -mx-[0.1em] -my-[0.2em]', className)}
      style={style}
    >
      {/* Real text, revealed left → right */}
      <span
        className={cn(
          'inline-block',
          LAYER_PAD,
          fillClass,
          inView && 'animate-[chroma-reveal_0.9s_ease-in-out_0.1s_forwards]'
        )}
        style={{ ...maskLayer(REVEAL_MASK), ...fillStyle }}
      >
        {children}
      </span>

      {/* Chroma band riding the reveal edge — present only for the duration of
          the sweep, so the glyphs are never duplicated in the delivered HTML or
          in the DOM at rest. */}
      {inView && !sweepDone && (
        <span
          aria-hidden
          // animationend bubbles, so an animated child inside the heading could
          // otherwise retire the band before its own sweep has finished.
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) setSweepDone(true)
          }}
          className={cn(
            'absolute inset-0 pointer-events-none select-none',
            LAYER_PAD,
            'bg-clip-text [-webkit-text-fill-color:transparent]',
            'animate-[chroma-band_0.9s_ease-in-out_0.1s_forwards]'
          )}
          style={{ ...maskLayer(BAND_MASK), backgroundImage: CHROMA_FILL }}
        >
          {children}
        </span>
      )}
    </span>
  )
}
